import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';
import '../config/echarts_theme';

// Import bar charts, all suffixed with Chart
import { BarChart } from 'echarts/charts';
import { LineChart } from 'echarts/charts';

// Import the tooltip, title, rectangular coordinate system, dataset and transform components
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
} from 'echarts/components';

// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { CanvasRenderer } from 'echarts/renderers';
import { service } from '@ember/service';

// Register the required components
echarts.use([
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  LegendComponent,
]);

function cleanup(instance) {
  instance?.chart?.dispose?.();
}

export default class CapacityChartModifier extends Modifier {
  @service intl;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  modify(element, [series], options) {
    this.options = options;

    if (this.chart == undefined) {
      this.setupChart(element);
      this.registerOnClick();
    }
    if (series != undefined) {
      this.updateChart(series, options);
    }
  }

  async setupChart(element) {
    this.chart = echarts.init(element, null, {
      height: 400,
    });
    const titleOptions = this.titleOptions;
    const tooltipOptions = this.tooltipOptions;
    const options = {
      ...titleOptions,
      ...tooltipOptions,
      ...this.chartOptions,
      yAxis: {},
    };

    // Display the chart using the configuration items and data just specified.
    this.chart.setOption(options);
  }

  get clickHandler() {
    const { onClick } = this.options;
    return onClick;
  }

  registerOnClick() {
    const handler = this.clickHandler;
    if (handler != undefined)
      this.chart.on('click', { targetType: 'axisLabel' }, handler);
  }

  get titleOptions() {
    const { title, subtitle } = this.options;
    let titleOptions = {};

    if (title != undefined || subtitle != undefined) {
      titleOptions = {
        title: {
          text: title,
          subtext: subtitle ?? '',
        },
      };
    }
    return titleOptions;
  }

  get tooltipOptions() {
    let context = this;
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 15,
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
          color: '#333',
        },
        extraCssText:
          'box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15); border-radius: 8px;',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
        formatter: function (params) {
          // For the new percentage-based visualization
          if (
            params[0].data &&
            typeof params[0].data === 'object' &&
            params[0].data.percentage !== undefined
          ) {
            const data = params[0].data;
            const week = params[0].axisValueLabel;
            const percentage = Math.round(data.raw_percentage);
            const totalWorkload = data.total_workload;
            const initiativeCapacity = data.initiative_capacity;
            const totalCapacity = data.total_capacity;
            const initiativePercentage =
              (initiativeCapacity / totalCapacity) * 100;

            // Determine status color and text
            let statusColor, statusText;
            if (percentage > 100) {
              statusColor = '#FF4136';
              statusText = 'Exceeding Total Capacity';
            } else if (percentage > initiativePercentage) {
              statusColor = '#FF851B';
              statusText = 'Using Flexible Capacity';
            } else {
              statusColor = '#2ECC40';
              statusText = 'Within Plannable Time';
            }

            let output = `<h3>Week: <span class="font-semibold">${week}</span></h3>`;
            output += `<h3 class="font-semibold">Workload: <span class="text-right font-bold">${percentage}%</span></h3>`;
            output += `<h3 class="font-semibold">Status: <span style="color: ${statusColor}; font-weight: bold;">${statusText}</span></h3>`;

            // Capacity breakdown
            output += '<h3 class="font-semibold mt-2">Capacity Breakdown</h3>';
            output += '<table class="w-full">';

            // Plannable time (initiative capacity)
            const plannableUsage = Math.min(totalWorkload, initiativeCapacity);
            const plannablePercentage =
              initiativeCapacity > 0
                ? Math.round((plannableUsage / initiativeCapacity) * 100)
                : 0;

            output += `<tr>
              <td class="pr-2">Plannable time</td>
              <td class="text-right font-bold tabular-nums">${Math.round(initiativeCapacity * 100) / 100} pw</td>
              <td class="text-right font-bold" style="color: ${plannablePercentage >= 100 ? '#FF851B' : '#2ECC40'}">
                (${plannablePercentage}% used)
              </td>
            </tr>`;

            // Flexible capacity
            const flexibleCapacity = totalCapacity - initiativeCapacity;
            const flexibleUsage = Math.max(
              0,
              Math.min(totalWorkload - initiativeCapacity, flexibleCapacity),
            );
            const flexiblePercentage =
              flexibleCapacity > 0
                ? Math.round((flexibleUsage / flexibleCapacity) * 100)
                : 0;

            output += `<tr>
              <td class="pr-2">Flexible</td>
              <td class="text-right font-bold tabular-nums">${Math.round(flexibleCapacity * 100) / 100} pw</td>
              <td class="text-right font-bold" style="color: ${flexiblePercentage > 0 ? '#FF851B' : '#2ECC40'}">
                (${flexiblePercentage}% used)
              </td>
            </tr>`;

            // Show overflow if exceeding capacity
            let overflow = Math.max(0, totalWorkload - totalCapacity);
            if (overflow > 0) {
              output += `<tr>
                <td class="pr-2">Exceeding Capacity</td>
                <td class="text-right font-bold tabular-nums" style="color: #FF4136">${Math.round(overflow * 100) / 100} pw</td>
                <td class="text-right font-bold" style="color: #FF4136">
                  (${Math.round((overflow / totalCapacity) * 100)}% over)
                </td>
              </tr>`;
            }

            output += `<tr style="border-top: 1px solid #ddd;">
              <td class="pr-2">Total Capacity</td>
              <td class="text-right font-bold tabular-nums">${Math.round(totalCapacity * 100) / 100} pw</td>
              <td></td>
            </tr>`;

            output += '</table>';

            // Add note about unspecified sources
            output +=
              '<p class="text-xs text-gray-500 mt-2 italic">Note: Workload includes all initiatives, including those without a specified source.</p>';

            return output;
          }

          // Fallback to original tooltip for backward compatibility
          var output = `<h3>Week: <span class="font-semibold">${params[0].axisValueLabel}</span></h3>`;

          output += '<h3 class="font-semibold mt-2">Investment Plan</h3>';
          output += '<table class="w-full">';

          params.reverse().forEach(function (param) {
            if (param.data != undefined) {
              const value = Math.round(param.data * 100) / 100;
              if (param.seriesType == 'line' && value !== undefined) {
                output += `<tr>
                  <td>${param.marker}</td>
                  <td class="pr-2">${param.seriesName}</td>
                  <td class="text-right font-bold tabular-nums">${value} pw</td>
                </tr>`;
              }
            }
          });

          output += '</table>';

          output += `<h3 class="font-semibold mt-2">${context.intl.t('_initiatives.title', { num: 2 })}</h3>`;

          output += '<table class="w-full">';

          params.reverse().forEach(function (param) {
            if (param.data != undefined) {
              const value = Math.round(param.data * 100) / 100;
              if (param.seriesType != 'line' && value !== undefined) {
                output += `<tr>
                  <td>${param.marker}</td>
                  <td class="pr-2">${param.seriesName}</td>
                  <td class="text-right font-bold tabular-nums">${value} pw</td>
                </tr>`;
              }
            }
          });

          return output + '</table>';
        },
      },
    };
  }

  updateChart(series) {
    this.chart.setOption(
      {
        series: series,
        ...this.chartOptions,
      },
      { replaceMerge: ['series'] },
    );
  }

  get chartOptions() {
    const chartOptions = {
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      animation: true,
      animationDuration: 800,
      animationEasing: 'elasticOut',
      grid: {
        left: '2%',
        right: '5%',
        bottom: '8%',
        top: '8%',
        containLabel: true,
      },
      legend: {
        show: true,
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
        },
        selectedMode: false,
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: function (value) {
          // Dynamically adjust max value to make highly overallocated weeks still visible
          return Math.max(value.max * 1.1, 120); // At least show 120% for context
        },
        name: 'Workload %',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontFamily: 'Arial',
          fontSize: 13,
          fontWeight: 'bold',
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#ccc',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(220, 220, 220, 0.5)',
            width: 1,
            type: 'dashed',
          },
        },
        axisLabel: {
          fontFamily: 'Arial',
          fontSize: 11,
          formatter: '{value}%',
        },
      },
      xAxis: {
        type: 'category',
        data: this.options.weeks,
        min: this.options.from,
        max: this.options.to,
        axisLine: {
          lineStyle: {
            color: '#ccc',
          },
        },
        axisLabel: {
          fontFamily: 'Arial',
          fontSize: 11,
          color: '#555',
          formatter: (value) => {
            const label = value.toString().slice(-2);
            if (value == this.options.weekToMark) {
              return `**${label}**\nnow`;
            } else {
              return label;
            }
          },
        },
        splitLine: {
          show: false,
        },
      },
    };

    return chartOptions;
  }
}
